import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Search, CornerDownLeft, Sparkles, FileText, Loader2 } from "lucide-react";

const TYPE_COLOR = { account: "#3E8F99", contact: "#E2711D", vendor: "#7A5CB2" };

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [ai, setAi] = useState(null);
  const [asking, setAsking] = useState(false);
  const [sel, setSel] = useState(0);
  const nav = useNavigate();

  const close = useCallback(() => { setOpen(false); setQ(""); setResults([]); setAi(null); setSel(0); }, []);

  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpen((o) => !o); }
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [close]);

  useEffect(() => {
    if (!open || !q.trim()) { setResults([]); setSel(0); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.post("/search", { q }); setResults(data.results); setSel(0); } catch { /* noop */ }
    }, 220);
    return () => clearTimeout(t);
  }, [q, open]);

  const goTo = (to) => { close(); nav(to); };
  const ask = async () => {
    if (!q.trim()) return;
    setAsking(true); setAi(null);
    try { const { data } = await api.post("/help/ask", { question: q }); setAi(data); }
    catch { setAi({ answer: "Help unavailable right now.", sources: [] }); }
    finally { setAsking(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-24 bg-black/50" onClick={close} data-testid="command-palette">
      <div className="bg-[#F5F3EE] border border-[#DFDAD0] w-full max-w-xl rounded-md shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-4 py-3 border-b border-[#DFDAD0] bg-white">
          <Search className="w-4 h-4 text-[#1C2530]/40" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(s + 1, results.length - 1)); }
              else if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
              else if (e.key === "Enter") { if (results.length > 0) goTo(results[Math.min(sel, results.length - 1)].to); else ask(); }
            }}
            placeholder="Jump to a lead, account, contact or vendor…"
            data-testid="palette-input" className="flex-1 bg-transparent outline-none text-sm text-[#1C2530]" />
          <kbd className="font-mono text-[9px] text-[#1C2530]/40 border border-[#DFDAD0] px-1.5 py-0.5 rounded">ESC</kbd>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {results.length > 0 && (
            <div className="py-1" data-testid="palette-results">
              <div className="px-4 py-1.5 font-mono text-[9px] uppercase tracking-wider text-[#1C2530]/40 flex items-center gap-1.5"><CornerDownLeft className="w-3 h-3" /> ↑↓ to move · ↵ to open · click a row</div>
              {results.map((r, i) => (
                <button key={i} onClick={() => goTo(r.to)} onMouseEnter={() => setSel(i)} data-testid={`palette-result-${i}`}
                  aria-selected={sel === i}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left ${sel === i ? "bg-[#EFECE5]" : "hover:bg-[#EFECE5]"}`}>
                  <span className="font-mono text-[8px] uppercase px-1.5 py-0.5 rounded" style={{ color: TYPE_COLOR[r.type], background: `${TYPE_COLOR[r.type]}15` }}>{r.type}</span>
                  <span className="flex-1"><span className="text-sm text-[#1C2530]">{r.label}</span><span className="block text-xs text-[#1C2530]/50">{r.sub}</span></span>
                  <CornerDownLeft className="w-3.5 h-3.5 text-[#1C2530]/30" />
                </button>
              ))}
            </div>
          )}
          <button onClick={ask} data-testid="palette-ask-ai" className="w-full flex items-center gap-2 px-4 py-3 border-t border-[#DFDAD0] hover:bg-[#EFECE5] text-left">
            <Sparkles className="w-4 h-4 text-[#7A5CB2]" />
            <span className="text-sm text-[#1C2530]">Ask AI Help{q.trim() ? `: "${q}"` : ""}</span>
            {asking && <Loader2 className="w-4 h-4 animate-spin text-[#7A5CB2] ml-auto" />}
          </button>
          {ai && (
            <div className="px-4 py-3 border-t border-[#DFDAD0] bg-[#7A5CB2]/5" data-testid="palette-ai-answer">
              <div className="text-sm text-[#1C2530] whitespace-pre-line">{ai.answer}</div>
              {ai.sources?.length > 0 && (
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  {ai.sources.map((s) => <span key={s} className="flex items-center gap-1 font-mono text-[9px] uppercase text-[#1C2530]/50"><FileText className="w-3 h-3" /> {s}</span>)}
                </div>
              )}
            </div>
          )}
          {!results.length && !ai && !q && (
            <div className="px-4 py-6 text-center font-mono text-[11px] text-[#1C2530]/40">Type to search records · ↵ opens the top match · Ask AI Help below · ⌘K to toggle</div>
          )}
        </div>
      </div>
    </div>
  );
}
